import { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { sendOtp, resetPassword } from "../../api/api";
import { showSnackbar } from "../../store/snackbarSlice";

import "./forgot.css";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    email: "",
    otp: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  // 🔥 TIMER STATE
  const [timer, setTimer] = useState(0);

  // 🔥 COUNTDOWN LOGIC
  useEffect(() => {
    if (timer === 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  // 🔥 HANDLE CHANGE
  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      [key]:
        key === "email"
          ? value.trim().toLowerCase()
          : value,
    }));

    setErrors((prev: any) => ({ ...prev, [key]: "" }));
  };

  // 🔥 VALIDATION
  const validateStep1 = () => {
    const newErrors: any = {};

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)
    ) {
      newErrors.email = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: any = {};

    if (!form.otp) {
      newErrors.otp = "OTP is required";
    } else if (form.otp.length !== 6) {
      newErrors.otp = "OTP must be 6 digits";
    }

    if (!form.password) {
      newErrors.password = "Password is required";
    } else if (form.password.length < 6) {
      newErrors.password = "Minimum 6 characters required";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirm password required";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔥 SEND OTP
  const handleSendOtp = async () => {
    if (!validateStep1()) return;

    try {
      setLoading(true);

      await sendOtp({ email: form.email });

      dispatch(
        showSnackbar({
          message: "OTP sent to your email 📩",
          type: "success",
        })
      );

      setStep(2);
      setTimer(60); // 🔥 start countdown
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message:
            err?.message ||
            err?.response?.data?.msg ||
            "Failed to send OTP",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // 🔥 RESET PASSWORD
  const handleReset = async () => {
    if (!validateStep2()) return;

    try {
      setLoading(true);

      await resetPassword({
        email: form.email,
        otp: form.otp,
        password: form.password,
      });

      dispatch(
        showSnackbar({
          message: "Password updated successfully 🎉",
          type: "success",
        })
      );

      navigate("/");
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message:
            err?.message ||
            err?.response?.data?.msg ||
            "Reset failed",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="forgot-container">
      <Paper elevation={0} className="forgot-card">

        <Typography variant="h5" className="forgot-title">
          Reset Password 🔐
        </Typography>

        <Typography className="forgot-subtitle">
          {step === 1
            ? "Enter your email to receive OTP"
            : "Enter OTP and new password"}
        </Typography>

        {/* STEP 1 */}
        {step === 1 && (
          <>
            <TextField
              placeholder="Email Address"
              fullWidth
              size="small"
              className="forgot-input"
              value={form.email}
              error={!!errors.email}
              helperText={errors.email}
              onChange={(e) =>
                handleChange("email", e.target.value)
              }
            />

            <Button
              fullWidth
              className="forgot-btn"
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send OTP"}
            </Button>
          </>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <>
            <TextField
              placeholder="OTP"
              fullWidth
              size="small"
              className="forgot-input"
              value={form.otp}
              error={!!errors.otp}
              helperText={errors.otp}
              onChange={(e) =>
                handleChange("otp", e.target.value)
              }
            />

            <TextField
              placeholder="New Password"
              type="password"
              fullWidth
              size="small"
              className="forgot-input"
              value={form.password}
              error={!!errors.password}
              helperText={errors.password}
              onChange={(e) =>
                handleChange("password", e.target.value)
              }
            />

            <TextField
              placeholder="Confirm Password"
              type="password"
              fullWidth
              size="small"
              className="forgot-input"
              value={form.confirmPassword}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              onChange={(e) =>
                handleChange("confirmPassword", e.target.value)
              }
            />

            <Button
              fullWidth
              className="forgot-btn"
              onClick={handleReset}
              disabled={loading}
            >
              {loading ? "Updating..." : "Reset Password"}
            </Button>

            {/* 🔥 TIMER / RESEND */}
            <Box sx={{ textAlign: "center", mt: 1 }}>
              {timer > 0 ? (
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                  Resend OTP in {Math.floor(timer / 60)}:
                  {(timer % 60).toString().padStart(2, "0")}
                </Typography>
              ) : (
                <Typography
                  className="forgot-link"
                  onClick={handleSendOtp}
                >
                  Resend OTP
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* BACK */}
        <Box className="forgot-footer">
          <Typography
            className="forgot-link"
            onClick={() => navigate("/")}
          >
            Back to Login
          </Typography>
        </Box>

      </Paper>
    </Box>
  );
};

export default ForgotPassword;